import jsQR from 'jsqr'
import React, { useEffect, useRef, useState, useCallback } from 'react'

/**
 * JsQRScanner — lightweight webcam QR scanner using jsQR (canvas-based).
 *
 * Props:
 *   onScan(text)  — called once when a QR code is successfully decoded
 *   onClose()     — called when the user clicks Cancel
 *   label         — optional label shown above the viewfinder
 *
 * FIX: inversionAttempts changed from 'dontInvert' to 'attemptBoth' so QR codes
 * displayed on dark screens (like this modal) can be scanned reliably.
 */
export const JsQRScanner = ({ onScan, onClose, label = 'Scan QR Code' }) => {
  const videoRef   = useRef(null)
  const canvasRef  = useRef(null)
  const rafRef     = useRef(null)
  const streamRef  = useRef(null)
  const doneRef    = useRef(false)

  const [status,   setStatus]   = useState('starting')  // starting | scanning | error
  const [errorMsg, setErrorMsg] = useState('')

  const stop = useCallback(() => {
    cancelAnimationFrame(rafRef.current)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  const tick = useCallback(() => {
    const video  = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(tick)
      return
    }

    canvas.width  = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

    // FIX: was 'dontInvert' — QR codes on dark phone screens require inversion to scan
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'attemptBoth',
    })

    if (code && code.data && !doneRef.current) {
      doneRef.current = true
      stop()
      onScan(code.data)
      return
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [onScan, stop])

  useEffect(() => {
    let cancelled = false

    const start = async () => {
      try {
        // Prefer back camera on phones, fallback to any camera
        let stream
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }
          })
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({ video: true })
        }

        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }

        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.setAttribute('playsinline', true)
          await videoRef.current.play()
        }

        setStatus('scanning')
        rafRef.current = requestAnimationFrame(tick)
      } catch (e) {
        if (!cancelled) {
          const msg = e.name === 'NotAllowedError'
            ? 'Camera permission denied — tap the 🔒 lock icon in your browser and allow Camera'
            : e.name === 'NotFoundError'
            ? 'No camera found on this device'
            : `Camera error: ${e.message}`
          setStatus('error')
          setErrorMsg(msg)
        }
      }
    }

    start()
    return () => {
      cancelled = true
      stop()
    }
  }, [tick, stop])

  const handleCancel = () => {
    stop()
    onClose()
  }

  return (
    <div style={wrapStyle}>
      <div style={headerStyle}>
        <span style={{ fontWeight: '700', fontSize: '14px' }}>{label}</span>
        <button onClick={handleCancel} style={closeBtn}>✕ Cancel</button>
      </div>

      {status === 'starting' && (
        <div style={statusBox}>
          <div style={spinner} />
          <span style={{ color: '#aaa', fontSize: '13px' }}>Starting camera...</span>
        </div>
      )}

      {status === 'error' && (
        <div style={errorBox}>
          <p style={{ margin: '0 0 12px 0', color: '#dc3545', fontSize: '14px' }}>{errorMsg}</p>
          <button onClick={handleCancel} style={cancelBtn}>Close</button>
        </div>
      )}

      {/* Video + hidden canvas for pixel reads */}
      <div style={{ display: status === 'scanning' ? 'block' : 'none', position: 'relative' }}>
        <video
          ref={videoRef}
          style={videoStyle}
          muted
          playsInline
          autoPlay
        />
        {/* Viewfinder overlay */}
        <div style={viewfinderStyle} />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <p style={{ textAlign: 'center', color: '#aaa', fontSize: '12px', margin: '8px 0 0 0' }}>
          Point camera at the QR code
        </p>
      </div>

      {/* Spinner keyframe — injected inline so no external CSS needed */}
      <style>{`@keyframes jsqr-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const wrapStyle       = { backgroundColor: '#111', border: '1px solid #333', borderRadius: '10px', overflow: 'hidden', width: '100%', maxWidth: '360px', margin: '0 auto' }
const headerStyle     = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #222' }
const closeBtn        = { padding: '4px 10px', backgroundColor: 'transparent', border: '1px solid #444', borderRadius: '4px', color: '#aaa', cursor: 'pointer', fontSize: '12px' }
const statusBox       = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '40px 20px' }
const errorBox        = { padding: '20px', textAlign: 'center' }
const cancelBtn       = { padding: '8px 20px', backgroundColor: '#333', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }
const videoStyle      = { width: '100%', display: 'block', maxHeight: '280px', objectFit: 'cover' }
const viewfinderStyle = { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '180px', height: '180px', border: '2px solid #007bff', borderRadius: '8px', boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)', pointerEvents: 'none' }
const spinner         = { width: '28px', height: '28px', border: '3px solid #333', borderTop: '3px solid #007bff', borderRadius: '50%', animation: 'jsqr-spin 0.8s linear infinite' }